import { useState, useEffect } from 'react';
import { useUpdateClientMutation } from '../../services/hooks/useClientes';
import { Cliente } from '../../types/clientes';
import { Loader2, X } from 'lucide-react';
import { toast } from 'react-toastify';
import { queryClient } from '../../main';
import InfoTooltip from '../common/InfoTooltip';
import { useSafeMounted } from '../../services/hooks/useSafeMounted';


interface EditarDatosPersonalesProps {
    cliente: Cliente;
    clientId: string;
    onClose: () => void;
}

const EditarDatosPersonales: React.FC<EditarDatosPersonalesProps> = ({ 
    cliente, 
    clientId,
    onClose 
}) => {

    const isMounted = useSafeMounted();
    // Si el componente está desmontado, no renderizar nada
    if (!isMounted.current) return null;

    const [formData, setFormData] = useState<Partial<Cliente>>({});
    const updateClientMutation = useUpdateClientMutation();
    
    // Inicializar el formulario con los datos del cliente
    useEffect(() => {
        setFormData({
            nombre: cliente.nombre,
            dni: cliente.dni,
            email: cliente.email,
            telefono: cliente.telefono,
            observaciones: cliente.observaciones,
            activo: cliente.activo,
            fechaNacimiento: cliente.fechaNacimiento || null
        });
    }, [cliente]);
    
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        try {
            await updateClientMutation.mutateAsync({ 
                id: clientId, 
                data: formData 
            });
            
            // Invalidar la consulta para refrescar los datos (formato correcto)
            queryClient.invalidateQueries({ 
                queryKey: ['client', clientId] 
            });
            
            toast.success('Datos actualizados correctamente');
            onClose();
        } catch (error) {
            console.error(error);
            toast.error('Error al actualizar los datos');
        }
    };
    
    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-2xl">
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-xl font-bold">Editar datos personales</h3>
                    <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
                        <X size={20} />
                    </button>
                </div>
                
                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700">Nombre</label>
                        <input
                            type="text"
                            value={formData.nombre || ''}
                            onChange={(e) => setFormData({...formData, nombre: e.target.value})}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3"
                            required
                        />
                    </div>
                    
                    <div>
                        <label className="block text-sm font-medium text-gray-700">DNI</label>
                        <input
                            type="text"
                            value={formData.dni || ''}
                            onChange={(e) => setFormData({...formData, dni: e.target.value})}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3"
                            required
                        />
                    </div>
                    
                    <div>
                        <label className="block text-sm font-medium text-gray-700">Email</label>
                        <input
                            type="email"
                            value={formData.email || ''}
                            onChange={(e) => setFormData({...formData, email: e.target.value})}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3"
                        />
                    </div>
                    
                    <div>
                        <label className="block text-sm font-medium text-gray-700">Teléfono</label>
                        <input
                            type="tel"
                            value={formData.telefono || ''}
                            onChange={(e) => setFormData({...formData, telefono: e.target.value})}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3"
                            required
                        />
                    </div>
                    
                    <div>
                        <label className="block text-sm font-medium text-gray-700">Fecha de Nacimiento</label>
                        <input
                            type="date"
                            value={formData.fechaNacimiento ? new Date(formData.fechaNacimiento).toISOString().split('T')[0] : ''}
                            onChange={(e) => setFormData({...formData, fechaNacimiento: e.target.value})}
                            max={new Date().toISOString().split('T')[0]}
                            min="1900-01-01"
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3"
                        />
                        {!formData.fechaNacimiento && (
                            <p className="mt-1 text-sm text-yellow-600">⚠️ Sin fecha de nacimiento registrada</p>
                        )}
                    </div>
                    
                    <div>
                        <div className='flex gap-2 align-middle'>
                            <label className="block text-sm font-medium text-gray-700">Referencias:</label>
                            <InfoTooltip 
                                iconSize={14} 
                                text="Las referencias son permanentes en el tiempo. No se borran por si solas"
                                position='bottom' 
                            />
                        </div>
                        
                        <textarea
                            value={formData.observaciones || ''}
                            onChange={(e) => setFormData({...formData, observaciones: e.target.value})}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3"
                            rows={3}
                        />
                    </div>
                    
                    <div className="flex justify-end space-x-2 pt-4">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                        >
                            Cancelar
                        </button>
                        <button
                            type="submit"
                            disabled={updateClientMutation.isPending}
                            className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 flex items-center"
                        >
                            {updateClientMutation.isPending && <Loader2 size={16} className="animate-spin mr-2" />}
                            Guardar cambios
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default EditarDatosPersonales;